import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { fetchData } from '../utils/apiHelpers';
import { getAll, update, create, deleteOne } from '../api';
import { signUpUser } from '../auth/authApi';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { generateStrongPassword, isPasswordStrong } from '../utils/passwordUtils';
import { useFormBuilder } from '../utils/useFormBuilder';
import { formatDepartmentOption } from '../utils/formatDepartmentOption';
import { enhanceHierarchyOptions } from '../utils/hierarchy';
import { useValidator } from '../utils/useValidator';
import Select from 'react-select';
import { HIERARCHY_STRUCTURE_DROPDOWN_API } from '../constants/hierarchy';
import {
  getEmailGroups,
  createEmailGroup,
  updateEmailGroup,
  deleteEmailGroup,
} from '../api/email-groupAPI';

const USER_OPTION_PREFIX = 'user:';
const CUSTOM_OPTION_PREFIX = 'custom:';
const ADD_EXTERNAL_OPTION_VALUE = 'add-external';

const createEmptyEmailEntry = () => ({
  type: null,
  userId: null,
  name: '',
  email: '',
  customId: null,
});

const UserListLayer = ({ mode = 'users' }) => {
  const isUsersMode = mode === 'users';
  const isEmailGroupsMode = mode === 'emailGroups';
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [emailGroupSearchTerm, setEmailGroupSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [emailGroups, setEmailGroups] = useState([]);
  const [teams, setTeams] = useState([]);
  const [assetGroups, setAssetGroups] = useState([]);
  const [assetClasses, setAssetClasses] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [newUser, setNewUser] = useState({ assignable: 1 });
  const [formReadOnly, setFormReadOnly] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordEnabled, setPasswordEnabled] = useState(true);
  const [trades, setTrades] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [allDepartments, setAllDepartments] = useState(true);
  const [showEmailGroupModal, setShowEmailGroupModal] = useState(false);
  const [editingEmailGroupId, setEditingEmailGroupId] = useState(null);
  const [emailGroupForm, setEmailGroupForm] = useState({
    groupName: '',
    entries: [createEmptyEmailEntry(), createEmptyEmailEntry()],
    groupIds: [],
    classIds: [],
    departmentIds: [],
    allGroups: false,
    allClasses: false,
    allDepartments: false,
  });
  const [customEmailOptions, setCustomEmailOptions] = useState([]);
  const [pendingCustomIndex, setPendingCustomIndex] = useState(null);
  const [showCustomEmailModal, setShowCustomEmailModal] = useState(false);
  const [customEmailForm, setCustomEmailForm] = useState({ name: '', email: '' });
  const [customEmailErrors, setCustomEmailErrors] = useState({});
  const isCurrentUserSuperAdmin = userProfile?.roles?.some(r => r.id === 1);

  const filteredUsers = useMemo(() => {
    if (isCurrentUserSuperAdmin) return users;
    return users.filter((user) => user.systemUser !== 1);
  }, [users, isCurrentUserSuperAdmin]);

  const validationFields = useMemo(
    () => [
      { field: 'name', label: 'Name', required: true },
      { field: 'email', label: 'Email', required: true, type: 'email' },
      { field: 'roleIds', label: 'Roles', required: true },
    ],
    [],
  );

  const { errors, setErrors, validate } = useValidator(validationFields, newUser);
  const {
    errors: validationErrors,
    setErrors: setValidationErrors,
    validateDepartmentsSelection,
    resetErrors: resetValidationErrors,
  } = useValidator();

  const emailGroupValidationFields = useMemo(
    () => [
      { field: 'groupName', label: 'Group Name', required: true },
    ],
    [],
  );

  const {
    errors: emailGroupFormErrors,
    setErrors: setEmailGroupFormErrors,
    validate: validateEmailGroup,
  } = useValidator(emailGroupValidationFields, emailGroupForm);

  const showUploadError = () => {
    alert('Upload failed. Please try again.');
  };

  const handleGeneratePassword = () => {
    const pwd = generateStrongPassword();
    setNewUser((prev) => ({ ...prev, password: pwd }));
    setShowPassword(true);
  };

  const tableRef = useRef(null);
  const dtRef = useRef(null);
  const emailGroupTableRef = useRef(null);
  const emailGroupDtRef = useRef(null);

  useEffect(() => {
    fetchData(getAll, { apiName: 'users' }, setUsers);
    fetchData(getAll, { apiName: 'roles' }, setRoles);
    fetchData(getAll, { apiName: 'user-roles' }, setUserRoles);
    fetchData(getAll, { apiName: 'user-profile' }, setUserProfile);
    fetchData(getAll, { apiName: 'teams' }, setTeams);
    fetchData(getAll, { apiName: 'groups' }, setAssetGroups);
    fetchData(getAll, { apiName: 'class' }, setAssetClasses);
    fetchData(getAll, { apiName: 'trades' }, setTrades);
    fetchData(getAll, { apiName: 'technicians' }, setTechnicians);
    fetchData(getAll, { apiName: HIERARCHY_STRUCTURE_DROPDOWN_API }, setDepartments);
  }, []);

  const loadEmailGroups = useCallback(async () => {
    try {
      const data = await getEmailGroups();
      setEmailGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching email groups:', error);
    }
  }, []);

  useEffect(() => {
    loadEmailGroups();
  }, [loadEmailGroups]);

  useEffect(() => {
    if (!isUsersMode) {
      return;
    }

    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }

    if (users.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        columnDefs: [{ orderable: false, targets: 0 }],
        info: false,
        responsive: false,
        language: {
          paginate: {
            first: '',
            last: '',
            next: '',
            previous: '',
          },
        },
      });
    }

    return () => {
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
    };
  }, [users, isUsersMode]);

  useEffect(() => {
    if (isUsersMode && dtRef.current) {
      dtRef.current.search(userSearchTerm).draw();
    }
  }, [userSearchTerm, isUsersMode]);

  useEffect(() => {
    if (!isEmailGroupsMode) {
      if (emailGroupDtRef.current) {
        emailGroupDtRef.current.destroy();
        emailGroupDtRef.current = null;
      }
      return;
    }

    if (emailGroupDtRef.current) {
      emailGroupDtRef.current.destroy();
      emailGroupDtRef.current = null;
    }

    if (emailGroups.length > 0) {
      emailGroupDtRef.current = $(emailGroupTableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        columnDefs: [{ orderable: false, targets: 2 }],
        info: false,
        responsive: false,
        language: {
          paginate: {
            first: '',
            last: '',
            next: '',
            previous: '',
          },
        },
      });
    }

    return () => {
      if (emailGroupDtRef.current) {
        emailGroupDtRef.current.destroy();
        emailGroupDtRef.current = null;
      }
    };
  }, [emailGroups, isEmailGroupsMode]);

  useEffect(() => {
    if (isEmailGroupsMode && emailGroupDtRef.current) {
      emailGroupDtRef.current.search(emailGroupSearchTerm).draw();
    }
  }, [emailGroupSearchTerm, isEmailGroupsMode]);

  const departmentOptions = useMemo(() => {
    if (!departments?.length) return [];
    const base = departments.map(d => ({
      value: d.id,
      label: d.name,
      parentId: d.parentId,
    }));
    return enhanceHierarchyOptions(base).options;
  }, [departments]);

  const userFields = useMemo(() => {
    const isSuperAdmin = userProfile?.roles?.some(r => r.id === 1);
    const roleOptions = (isSuperAdmin ? roles : roles.filter(r => r.id !== 1)).map(r => ({ value: r.id, label: r.name }));
    const fields = [
      { field: 'name', label: 'Name', type: 'text', size: '6', required: true, disabled: formReadOnly },
      { field: 'email', label: 'Email', type: 'text', size: '6', required: true, disabled: formReadOnly },
      { field: 'roleIds', label: 'Roles', type: 'multi-select', size: '12', options: roleOptions, disabled: formReadOnly, required: true },
      {
        field: 'departments',
        label: 'Departments',
        type: 'multi-select',
        size: '6',
        options: departmentOptions,
        formatOptionLabel: formatDepartmentOption,
        disabled: formReadOnly || allDepartments,
      },
      { field: 'allDepartments', label: 'All Departments', type: 'switch', size: '6', disabled: formReadOnly },
      { field: 'assignable', label: 'Assignable to Work Orders', type: 'switch', size: '6', disabled: formReadOnly },
      { field: 'active', label: 'Active', type: 'switch', size: '6', disabled: formReadOnly },
      ...(isSuperAdmin ? [{ field: 'systemUser', label: 'System User', type: 'switch', size: '6', disabled: formReadOnly }] : []),
      { field: 'userBio', label: 'Bio / Notes', type: 'textarea', size: '12', disabled: formReadOnly },
    ];
    if (newUser.assignable === 1) {
      fields.push(
        { field: 'teamIds', label: 'Assign Team(s)', type: 'multi-select', size: '12', options: teams.map(t => ({ value: t.id, label: t.teamName })), disabled: formReadOnly },
        { field: 'tradeIds', label: 'Assign Trade(s)', type: 'multi-select', size: '12', options: trades.map(t => ({ value: t.id, label: t.name })), disabled: formReadOnly }
      );
    }
    return fields;
  }, [roles, departmentOptions, teams, trades, newUser.assignable, formReadOnly, allDepartments, userProfile]);

  const formErrors = useMemo(() => ({ ...errors, ...validationErrors }), [errors, validationErrors]);

  const { renderField } = useFormBuilder(userFields, newUser, setNewUser, formErrors);

  useEffect(() => {
    setNewUser(prev => {
      const updated = { ...prev, allDepartments: allDepartments ? 1 : 0 };
      if (allDepartments && prev.departments?.[0] !== -1) {
        updated.departments = [-1];
      }
      if (!allDepartments && prev.departments?.length === 1 && prev.departments[0] === -1) {
        updated.departments = [];
      }
      return updated;
    });
  }, [allDepartments]);

  useEffect(() => {
    if (typeof newUser.allDepartments !== 'undefined') {
      setAllDepartments(Boolean(newUser.allDepartments));
    }
  }, [newUser.allDepartments]);

  useEffect(() => {
    if (allDepartments || (Array.isArray(newUser.departments) && newUser.departments.length > 0)) {
      setValidationErrors((prev) => {
        if (!prev.departments) {
          return prev;
        }
        const next = { ...prev };
        delete next.departments;
        return next;
      });
    }
  }, [allDepartments, newUser.departments, setValidationErrors]);

  // Toggle active flag in-place
  /*const handleActiveToggle = async (userId, currentlyActive) => {
    const newActive = currentlyActive ? 0 : 1;
    await update('users', userId, { active: newActive });
  
    setUsers(prevUsers =>
      prevUsers.map(u =>
        u.id === userId ? { ...u, active: newActive } : u
      )
    );

    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
  };*/

  /* const handleAssignableToggle = async (userId, currentlyAssignable) => {
    const newAssignable = currentlyAssignable ? 0 : 1;
    await update('users', userId, { assignable: newAssignable });

    setUsers(prevUsers =>
      prevUsers.map(u =>
        u.id === userId ? { ...u, assignable: newAssignable } : u
      )
    );

    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
  }; */

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleView = (user) => {
    setErrors({});
    resetValidationErrors();
    setFormReadOnly(true);
    const isSuperAdmin = userProfile?.roles?.some(r => r.id === 1);
    const assigned = userRoles
      .filter(ur => ur.userId === user.id)
      .map(ur => roles.find(r => r.id === ur.roleId))
      .filter(Boolean)
      .map(r => r.id)
      .filter(id => isSuperAdmin || id !== 1);
    const tech = technicians.find(t => t.userId === user.id);
    const allDeps = user.departments?.[0] === -1;
    setNewUser({
      ...user,
      assignable: user.assignable ?? 1,
      password: '',
      roleIds: assigned,
      teamIds: tech?.teams || [],
      tradeIds: tech?.trades || [],
      departments: user.departments || [-1],
      allDepartments: allDeps ? 1 : 0,
    });
    setAllDepartments(allDeps);
    setShowPassword(false);
    setPasswordEnabled(false);
    setEditingUserId(user.id);
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setErrors({});
    resetValidationErrors();
    setFormReadOnly(false);
    const isSuperAdmin = userProfile?.roles?.some(r => r.id === 1);
    const assigned = userRoles
      .filter(ur => ur.userId === user.id)
      .map(ur => roles.find(r => r.id === ur.roleId))
      .filter(Boolean)
      .map(r => r.id)
      .filter(id => isSuperAdmin || id !== 1);
    const tech = technicians.find(t => t.userId === user.id);
    const allDeps = user.departments?.[0] === -1;
    setNewUser({
      ...user,
      assignable: user.assignable ?? 1,
      password: '',
      roleIds: assigned,
      teamIds: tech?.teams || [],
      tradeIds: tech?.trades || [],
      departments: user.departments || [-1],
      allDepartments: allDeps ? 1 : 0,
    });
    setAllDepartments(allDeps);
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    setEditingUserId(user.id);
    setShowPassword(false);
    setPasswordEnabled(false);
    setShowModal(true);
  };

  const handleCreate = () => {
    setErrors({});
    resetValidationErrors();
    setFormReadOnly(false);
    setNewUser({
      name: '',
      email: '',
      password: '',
      active: 1,
      assignable: 1,
      orgId: 1,
      roleIds: [],
      teamIds: [],
      tradeIds: [],
      departments: [-1],
      allDepartments: 1,
      systemUser: 0,
    });
    setAllDepartments(true);
    setEditingUserId(null);
    setShowPassword(false);
    setPasswordEnabled(true);
    setShowModal(true);
  };

  const handleSave = async () => {
    const baseValid = validate();
    const departmentsValid = validateDepartmentsSelection({
      allDepartments,
      departments: newUser.departments,
    });
    if (!baseValid || !departmentsValid) return;
    if (!editingUserId && !newUser.password) {
      setErrors((prev) => ({ ...prev, password: 'Password is required' }));
      return;
    } else if (newUser.password && !isPasswordStrong(newUser.password)) {
      setErrors((prev) => ({ ...prev, password: 'Password must be 8+ chars, include uppercase and symbol' }));
      return;
    }
    // Prevent duplicate email from triggering server error
    const emailToCheck = newUser.email?.trim().toLowerCase();
    const duplicate = users.some(
      u => u.email?.trim().toLowerCase() === emailToCheck && u.id !== editingUserId
    );
    if (duplicate) {
      setErrors(prev => ({ ...prev, email: 'Email already exists' }));
      return;
    }

    const isSuperAdmin = userProfile?.roles?.some(r => r.id === 1);
    const payload = {
      name: newUser.name,
      email: newUser.email,
      active: newUser.active ? 1 : 0,
      assignable: newUser.assignable ? 1 : 0,
      userBio: newUser.userBio || '',
      userImage: newUser.userImage || '',
      departments:
        newUser.departments && newUser.departments.length
          ? newUser.departments
          : [-1],
      orgId: userProfile?.org_id,
      roleIds: (isSuperAdmin ? newUser.roleIds : newUser.roleIds?.filter(id => id !== 1)) || [],
      ...(isSuperAdmin ? { systemUser: newUser.systemUser ? 1 : 0 } : {}),
    };

    let savedUserId = editingUserId;
    try {
      if (editingUserId) {
        if (passwordEnabled && newUser.password) {
          payload.passwordHash = newUser.password;
        }
        await update('users', editingUserId, payload);
        savedUserId = editingUserId;
      } else {
        await signUpUser({
          org_id: userProfile?.org_id,
          name: newUser.name,
          email: newUser.email,
          password: newUser.password,
          database: userProfile?.db,
        });
        const allUsers = await getAll('users');
        const createdUser = allUsers.find((u) => u.email === newUser.email);
        if (!createdUser) {
          throw new Error('Failed to retrieve created user');
        }
        savedUserId = createdUser.id;
        await update('users', savedUserId, payload);
      }
    } catch (error) {
      if (error.message?.includes('Email')) {
        setErrors((prev) => ({ ...prev, email: error.message }));
      } else {
        console.error('Error saving user:', error);
      }
      return;
    }
    fetchData(getAll, { apiName: 'user-roles' }, setUserRoles);

    if (newUser.assignable === 1) {
      const techPayload = {
        userId: savedUserId,
        teams: newUser.teamIds || [],
        trades: newUser.tradeIds || [],
        active: newUser.active ? 1 : 0,
      };
      const existingTech = technicians.find(t => t.userId === savedUserId);
      if (existingTech) {
        await update('technicians', existingTech.id, techPayload);
      } else {
        await create('technicians', techPayload);
      }
      fetchData(getAll, { apiName: 'technicians' }, setTechnicians);
    }

    setShowModal(false);
    // repaint the table
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    fetchData(getAll, { apiName: 'users' }, setUsers);
  };

  // Validate and sanitize upload response
  const parseUploadResponse = async (res) => {
    try {
      const data = await res.json();
      const { key, url } = data || {};
      if (typeof key !== 'string') return null;
      // reject any key containing HTML brackets
      if (/[<>]/.test(key)) return null;
      return { key, url: typeof url === 'string' ? url : '' };
    } catch {
      return null;
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await fetchData(deleteOne, { apiName: 'users', id });
        if (dtRef.current) {
          dtRef.current.destroy();
          dtRef.current = null;
        }
        await fetchData(getAll, { apiName: 'users' }, setUsers);
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const resetEmailGroupState = useCallback(() => {
    setEmailGroupForm({
      groupName: '',
      entries: [createEmptyEmailEntry(), createEmptyEmailEntry()],
      groupIds: [],
      classIds: [],
      departmentIds: [],
      allGroups: false,
      allClasses: false,
      allDepartments: false,
    });
    setCustomEmailOptions([]);
    setPendingCustomIndex(null);
    setCustomEmailForm({ name: '', email: '' });
    setCustomEmailErrors({});
    setEmailGroupFormErrors({});
  }, [setEmailGroupFormErrors]);

  const handleOpenCreateEmailGroup = () => {
    resetEmailGroupState();
    setEditingEmailGroupId(null);
    setShowEmailGroupModal(true);
  };

  const handleOpenEditEmailGroup = (group) => {
    if (!group) {
      return;
    }
    const customOptions = [];
    const mappedEntries = (group.emails || []).map((entry, index) => {
      if (entry.userId) {
        return {
          type: 'user',
          userId: entry.userId,
          customId: null,
          name: entry.user?.name || '',
          email: entry.user?.email || '',
        };
      }
      const customId = `${group.id || 'group'}-${entry.id || index}`;
      const name = entry.name || '';
      const email = entry.email || '';
      customOptions.push({ id: customId, name, email });
      return {
        type: 'custom',
        userId: null,
        customId,
        name,
        email,
      };
    });

    const entries = mappedEntries.length
      ? mappedEntries.length === 1
        ? [...mappedEntries, createEmptyEmailEntry()]
        : mappedEntries
      : [createEmptyEmailEntry(), createEmptyEmailEntry()];

    setCustomEmailOptions(customOptions);
    const normalizedGroupIds = Array.isArray(group.groupIds)
      ? group.groupIds.map((value) => Number(value)).filter((value) => !Number.isNaN(value))
      : [];
    const normalizedClassIds = Array.isArray(group.classIds)
      ? group.classIds.map((value) => Number(value)).filter((value) => !Number.isNaN(value))
      : [];
    const normalizedDepartmentIds = Array.isArray(group.departmentIds)
      ? group.departmentIds.map((value) => Number(value)).filter((value) => !Number.isNaN(value))
      : [];
    const allGroups = normalizedGroupIds.includes(-1);
    const allClasses = normalizedClassIds.includes(-1);
    const allDepartmentsSelected = normalizedDepartmentIds.includes(-1);

    setEmailGroupForm({
      groupName: group.groupName || '',
      entries,
      groupIds: allGroups ? [] : normalizedGroupIds,
      classIds: allClasses ? [] : normalizedClassIds,
      departmentIds: allDepartmentsSelected ? [] : normalizedDepartmentIds,
      allGroups,
      allClasses,
      allDepartments: allDepartmentsSelected,
    });
    setEmailGroupFormErrors({});
    setEditingEmailGroupId(group.id);
    setPendingCustomIndex(null);
    setShowCustomEmailModal(false);
    setCustomEmailForm({ name: '', email: '' });
    setCustomEmailErrors({});
    setShowEmailGroupModal(true);
  };

  const handleCloseEmailGroupModal = () => {
    setShowEmailGroupModal(false);
    setEditingEmailGroupId(null);
    resetEmailGroupState();
  };

  const userEmailOptions = useMemo(
    () =>
      users.map((user) => ({
        value: `${USER_OPTION_PREFIX}${user.id}`,
        label: user.email || user.name || `User ${user.id}`,
        data: user,
      })),
    [users],
  );

  const externalEmailOptions = useMemo(
    () =>
      customEmailOptions.map((item) => ({
        value: `${CUSTOM_OPTION_PREFIX}${item.id}`,
        label: item.email,
        data: item,
      })),
    [customEmailOptions],
  );

  const emailSelectOptions = useMemo(
    () => [
      { value: ADD_EXTERNAL_OPTION_VALUE, label: '+ Add outside email address' },
      ...userEmailOptions,
      ...externalEmailOptions,
    ],
    [userEmailOptions, externalEmailOptions],
  );

  const groupSelectOptions = useMemo(
    () =>
      assetGroups.map((group) => ({
        value: group.id,
        label: group.groupName || group.groupDescription || `Group ${group.id}`,
      })),
    [assetGroups],
  );

  const classSelectOptions = useMemo(
    () =>
      assetClasses.map((assetClass) => ({
        value: assetClass.id,
        label: assetClass.className || `Class ${assetClass.id}`,
      })),
    [assetClasses],
  );

  const departmentSelectOptions = useMemo(
    () => departmentOptions.map((option) => ({ ...option })),
    [departmentOptions],
  );

  const resolveSelectedEmailOption = useCallback(
    (entry) => {
      if (entry.type === 'user' && entry.userId) {
        const user = users.find((u) => u.id === entry.userId);
        return {
          value: `${USER_OPTION_PREFIX}${entry.userId}`,
          label: user?.email || user?.name || `User ${entry.userId}`,
        };
      }
      if (entry.type === 'custom' && entry.customId) {
        const item = customEmailOptions.find((opt) => opt.id === entry.customId) || {
          id: entry.customId,
          email: entry.email,
        };
        return {
          value: `${CUSTOM_OPTION_PREFIX}${entry.customId}`,
          label: item.email,
        };
      }
      return null;
    },
    [customEmailOptions, users],
  );

  const handleEmailGroupNameChange = (event) => {
    const { value } = event.target;
    setEmailGroupForm((prev) => ({ ...prev, groupName: value }));
    setEmailGroupFormErrors((prev) => ({ ...prev, groupName: undefined, submit: undefined }));
  };

  const handleEmailSelectionChange = (index, option) => {
    if (!option) {
      setEmailGroupForm((prev) => {
        const entries = prev.entries.map((entry, idx) =>
          idx === index ? createEmptyEmailEntry() : entry,
        );
        return { ...prev, entries };
      });
      return;
    }

    if (option.value === ADD_EXTERNAL_OPTION_VALUE) {
      setPendingCustomIndex(index);
      setCustomEmailForm({ name: '', email: '' });
      setCustomEmailErrors({});
      setShowCustomEmailModal(true);
      return;
    }

    if (option.value.startsWith(USER_OPTION_PREFIX)) {
      const userId = Number(option.value.replace(USER_OPTION_PREFIX, ''));
      if (Number.isFinite(userId)) {
        setEmailGroupForm((prev) => {
          const entries = prev.entries.map((entry, idx) =>
            idx === index
              ? { type: 'user', userId, name: '', email: '', customId: null }
              : entry,
          );
          return { ...prev, entries };
        });
        setEmailGroupFormErrors((prev) => ({ ...prev, emails: undefined, submit: undefined }));
      }
      return;
    }

    if (option.value.startsWith(CUSTOM_OPTION_PREFIX)) {
      const customId = option.value.replace(CUSTOM_OPTION_PREFIX, '');
      const selected = customEmailOptions.find((item) => item.id === customId);
      if (selected) {
        setEmailGroupForm((prev) => {
          const entries = prev.entries.map((entry, idx) =>
            idx === index
              ? {
                type: 'custom',
                userId: null,
                customId,
                name: selected.name,
                email: selected.email,
              }
              : entry,
          );
          return { ...prev, entries };
        });
        setEmailGroupFormErrors((prev) => ({ ...prev, emails: undefined, submit: undefined }));
      }
    }
  };

  const handleAddEmailRow = () => {
    setEmailGroupForm((prev) => {
      if (prev.entries.length >= 25) {
        return prev;
      }
      return {
        ...prev,
        entries: [...prev.entries, createEmptyEmailEntry()],
      };
    });
  };

  const handleRemoveEmailRow = (index) => {
    setEmailGroupForm((prev) => {
      const entries = prev.entries.filter((_, idx) => idx !== index);
      if (entries.length === 0) {
        entries.push(createEmptyEmailEntry());
      }
      return { ...prev, entries };
    });
  };

  const handleCustomEmailModalClose = () => {
    setShowCustomEmailModal(false);
    setPendingCustomIndex(null);
    setCustomEmailForm({ name: '', email: '' });
    setCustomEmailErrors({});
  };

  const handleCustomEmailSave = () => {
    const name = customEmailForm.name.trim();
    const email = customEmailForm.email.trim();
    const errors = {};
    if (!name) {
      errors.name = 'Name is required';
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      errors.email = 'Email is required';
    } else if (!emailPattern.test(email)) {
      errors.email = 'Email must be valid';
    }

    if (Object.keys(errors).length) {
      setCustomEmailErrors(errors);
      return;
    }

    const customId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const newOption = { id: customId, name, email };
    setCustomEmailOptions((prev) => [...prev, newOption]);

    if (pendingCustomIndex !== null) {
      setEmailGroupForm((prev) => {
        const entries = prev.entries.map((entry, idx) =>
          idx === pendingCustomIndex
            ? {
              type: 'custom',
              userId: null,
              customId,
              name,
              email,
            }
            : entry,
        );
        return { ...prev, entries };
      });
    }

    setEmailGroupFormErrors((prev) => ({ ...prev, emails: undefined, submit: undefined }));
    handleCustomEmailModalClose();
  };

  const handleEmailGroupSave = async () => {
    const isValid = validateEmailGroup();
    if (!isValid) {
      return;
    }

    const payloadEmails = emailGroupForm.entries
      .map((entry) => {
        if (entry.type === 'user' && entry.userId) {
          return { userId: entry.userId };
        }
        if (entry.type === 'custom' && entry.email && entry.name) {
          return { email: entry.email.trim(), name: entry.name.trim() };
        }
        return null;
      })
      .filter((entry) => entry !== null);

    if (!payloadEmails.length) {
      setEmailGroupFormErrors((prev) => ({
        ...prev,
        emails: 'At least one email address must be added to the list.',
      }));
      return;
    }

    const payload = {
      groupName: emailGroupForm.groupName.trim(),
      emails: payloadEmails,
      groupIds: emailGroupForm.allGroups ? [-1] : emailGroupForm.groupIds,
      classIds: emailGroupForm.allClasses ? [-1] : emailGroupForm.classIds,
      departmentIds: emailGroupForm.allDepartments ? [-1] : emailGroupForm.departmentIds,
    };

    try {
      if (editingEmailGroupId) {
        await updateEmailGroup(editingEmailGroupId, payload);
      } else {
        await createEmailGroup(payload);
      }
      await loadEmailGroups();
      handleCloseEmailGroupModal();
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || 'Unable to save email group.';
      setEmailGroupFormErrors((prev) => ({ ...prev, submit: message }));
    }
  };

  const handleDeleteEmailGroup = async (group) => {
    if (!group?.id) {
      return;
    }
    if (!window.confirm(`Delete email group "${group.groupName}"?`)) {
      return;
    }
    try {
      await deleteEmailGroup(group.id);
      await loadEmailGroups();
    } catch (error) {
      console.error('Error deleting email group:', error);
    }
  };

  const formatEmailDisplay = useCallback(
    (emailEntry) => {
      if (emailEntry.userId) {
        const user = emailEntry.user || users.find((u) => u.id === emailEntry.userId);
        const name = user?.name || '';
        const email = user?.email || '';
        if (name && email) {
          return `${name} (${email})`;
        }
        return email || name || 'System User';
      }
      if (emailEntry.email && emailEntry.name) {
        return `${emailEntry.email} (${emailEntry.name})`;
      }
      return emailEntry.email || '';
    },
    [users],
  );

  const emailLimitReached = emailGroupForm.entries.length >= 25;


  return (
    <div className="card h-100 p-0 rounded-0 overflow-hidden">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between align-items-center">
        {isUsersMode ? (
          <>
            <input
              type="text"
              className="bg-base form-control w-auto"
              placeholder="Search users…"
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
            />
            {userProfile?.privileges?.includes('users:create') && (
              <button
                className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
                onClick={handleCreate}
              >
                <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                Add New User
              </button>
            )}
          </>
        ) : (
          <>
            <input
              type="text"
              className="bg-base form-control w-auto"
              placeholder="Search email groups…"
              value={emailGroupSearchTerm}
              onChange={(e) => setEmailGroupSearchTerm(e.target.value)}
            />
            {userProfile?.privileges?.includes('email-groups:create') && (
              <button
                className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
                onClick={handleOpenCreateEmailGroup}
              >
                <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                Add Email Group
              </button>
            )}
          </>
        )}
      </div>
      <div className="card-body p-0">
        {isUsersMode ? (
          <div key="users-table" className="table-responsive">
            <table
              key="users-table-inner"
              className="table bordered-table mb-0 w-100"
              ref={tableRef}
            >
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Departments</th>
                  <th>Active</th>
                  <th>Assignable</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const assigned = userRoles
                    .filter((ur) => ur.userId === user.id)
                    .map((ur) => roles.find((r) => r.id === ur.roleId))
                    .filter(Boolean);
                  const userIsSuperAdmin = assigned.some((r) => r.id === 1);

                  return (
                    <tr key={user.id}>
                      <td style={{ width: '60px' }}>
                        <img
                          src={
                            user.userImage === null || user.userImage === ''
                              ? `${process.env.REACT_APP_SPACES_BASE}/user.png`
                              : `${process.env.REACT_APP_SPACES_BASE}/${user.userImage}`
                          }
                          alt="User"
                          style={{
                            width: '45px',
                            height: '45px',
                            objectFit: 'cover',
                            borderRadius: '50%',
                            border: '1px solid #ddd',
                          }}
                        />
                      </td>
                      <td style={{ verticalAlign: 'middle' }}>{user.name}</td>
                      <td style={{ verticalAlign: 'middle' }}>{user.email}</td>
                      <td style={{ verticalAlign: 'middle' }}>
                        {assigned.map((r) => (
                          <span
                            key={r.id}
                            className="text-secondary-light border radius-4 px-8 my-4 text-sm fw-medium mx-2"
                          >
                            {r.name}
                          </span>
                        ))}
                      </td>
                      <td style={{ verticalAlign: 'middle' }}>
                        {user.departments?.[0] === -1 ? (
                          <span className="text-white bg-primary border radius-4 px-8 my-4 text-sm fw-medium mx-2">
                            All Departments
                          </span>
                        ) : (
                          departments
                            .filter((d) => user.departments?.includes(d.id))
                            .map((d) => (
                              <span
                                key={d.id}
                                className="text-white bg-primary border radius-4 px-8 my-4 text-sm fw-medium mx-2"
                              >
                                {d.name}
                              </span>
                            ))
                        )}
                      </td>
                      <td style={{ verticalAlign: 'middle' }}>
                        <div className="form-switch switch-primary d-flex align-items-center gap-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={user.active === 1}
                            disabled
                          />
                        </div>
                      </td>
                      <td style={{ verticalAlign: 'middle' }}>
                        <div className="form-switch switch-primary d-flex align-items-center gap-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={user.assignable === 1}
                            disabled
                          />
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          {userProfile?.privileges?.includes('users:view') && (
                            <button
                              className="w-32-px h-32-px bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              onClick={() => handleView(user)}
                            >
                              <Icon icon="ic:baseline-visibility" className="icon text-xl line-height-1" />
                            </button>
                          )}
                          {userProfile?.privileges?.includes('users:update') &&
                            (isCurrentUserSuperAdmin || !userIsSuperAdmin) && (
                              <button
                                className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center mx-6"
                                onClick={() => handleEdit(user)}
                              >
                                <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                              </button>
                            )}
                          {userProfile?.privileges?.includes('users:delete') && (
                            <button
                              className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              onClick={() => handleDelete(user.id)}
                            >
                              <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div key="email-groups-table" className="table-responsive">
            <table
              key="email-groups-table-inner"
              className="table bordered-table mb-0 w-100"
              ref={emailGroupTableRef}
            >
              <thead>
                <tr>
                  <th>Group Name</th>
                  <th>Emails</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {emailGroups.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-4 rounded-0">No Groups Setup</td>
                  </tr>
                )}
                {emailGroups.map((group) => (
                  <tr key={group.id}>
                    <td style={{ verticalAlign: 'middle' }}>{group.groupName}</td>
                    <td style={{ verticalAlign: 'middle', whiteSpace: 'normal' }}>
                      {(group.emails || []).map((email, index) => (
                        <div key={email.id ?? `${group.id}-${index}`} className="mb-2">
                          <span className="text-secondary-light border radius-4 px-8 py-4 text-sm fw-medium d-inline-block">
                            {formatEmailDisplay(email)}
                          </span>
                        </div>
                      ))}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        {userProfile?.privileges?.includes('email-groups:update') && (
                          <button
                            className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                            onClick={() => handleOpenEditEmailGroup(group)}
                          >
                            <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                          </button>
                        )}
                        {userProfile?.privileges?.includes('email-groups:delete') && (
                          <button
                            className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                            onClick={() => handleDeleteEmailGroup(group)}
                          >
                            <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingUserId ? (formReadOnly ? 'View User' : 'Edit User') : 'Add New User'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                {newUser.userImage && (
                  <div className="mb-3 mx-3 text-center">
                    <img
                      src={`${process.env.REACT_APP_SPACES_BASE}/${newUser.userImage}`}
                      alt="User"
                      style={{
                        width: '150px',
                        height: '150px',
                        objectFit: 'cover',
                        borderRadius: '50%',
                        border: '1px solid #ccc',
                      }}
                    />
                  </div>
                )}
                <div className="row g-3">{userFields.map(renderField)}</div>
                <div className="mb-3">
                  <label className="form-label">Password</label>
                  <div className="d-flex">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                      name="password"
                      value={newUser.password || ''}
                      onChange={handleInputChange}
                      disabled={!passwordEnabled || formReadOnly}
                    />
                    {!formReadOnly && passwordEnabled && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary ms-2"
                        onClick={handleGeneratePassword}
                      >
                        Generate
                      </button>
                    )}
                    {passwordEnabled && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-rounded shadow ms-2"
                        onClick={() => setShowPassword((p) => !p)}
                      >
                        <Icon
                          icon={showPassword ? 'ic:baseline-visibility-off' : 'ic:baseline-visibility'}
                          className="text-xl text-white"
                        />
                      </button>
                    )}
                    {editingUserId && !formReadOnly && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm ms-2 d-flex align-items-center gap-1"
                        onClick={() => setPasswordEnabled((p) => !p)}
                      >
                        <Icon icon="mdi:lock-open-outline" className="icon text-xl line-height-1" />
                        Unlock
                      </button>
                    )}
                  </div>
                  {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                </div>
              </div>
              <div className="mb-3 mx-3">
                <label className="form-label">Profile Image</label>
                <input
                  type="file"
                  name="userImage"
                  accept=".jpg,.jpeg,.png"
                  className="form-control"
                  disabled={formReadOnly}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file && file.size > 5 * 1024 * 1024) {
                      alert('File size must be less than 5MB');
                      return;
                    }

                    const formData = new FormData();
                    formData.append('file', file);

                    try {
                      const uploadRes = await fetch(`${process.env.REACT_APP_API_URL}/upload/userimages`, {
                        method: 'POST',
                        body: formData,
                        credentials: 'include',
                      });

                      const parsed = await parseUploadResponse(uploadRes);
                      if (parsed) {
                        setNewUser((prev) => ({
                          ...prev,
                          userImage: parsed.key,
                        }));
                      } else {
                        showUploadError();
                      }
                    } catch (error) {
                      console.error('Image Upload failed:', error);
                    }
                  }}
                />
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
                {!formReadOnly && (
                  <button className="btn btn-primary" onClick={handleSave}>
                    Save
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showEmailGroupModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content" style={{ marginTop: '5vh' }}>
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingEmailGroupId ? 'Edit Email Group' : 'Add Email Group'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseEmailGroupModal} />
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  Create distribution lists of system users or external contacts to send group
                  notifications from Samurai.
                </p>
                <div className="mb-3">
                  <label className="form-label">Group Name</label>
                  <input
                    type="text"
                    className={`form-control ${emailGroupFormErrors.groupName ? 'is-invalid' : ''}`}
                    value={emailGroupForm.groupName}
                    onChange={handleEmailGroupNameChange}
                    placeholder="Enter group name"
                  />

                  {emailGroupFormErrors.groupName && (
                    <div className="invalid-feedback">{emailGroupFormErrors.groupName}</div>
                  )}
                </div>
                <hr className="mt-36 mb-24"></hr>
                {/* Removing the group, class and department selection for now as I'm not sure it's needed. 
                Forms can already be restricted by group, class and department and adding a second restriction
                may confuse the user. Keep it simple until a client asks for this functionality.

                Note: there are no filters applied to the form distribution to handle group, class or department filtering.                
                
                <div className="mb-3">
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md-9">
                      <label className="form-label mb-3">Departments</label>
                      <Select
                        isMulti
                        classNamePrefix="react-select"
                        value={selectedDepartmentOptions}
                        onChange={handleDepartmentSelectChange}
                        options={departmentSelectOptions}
                        placeholder="Select departments"
                        isDisabled={emailGroupForm.allDepartments}
                        formatOptionLabel={formatDepartmentOption}
                      />
                    </div>
                    <div className="col-12 col-md-3 d-flex align-items-center justify-content-between justify-content-md-end gap-2 mt-2 mb-8 mt-md-0">
                      <span className="text-sm">All Departments</span>
                      <div className="form-switch switch-primary d-flex align-items-center gap-2 me-8">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={emailGroupForm.allDepartments}
                          onChange={handleAllDepartmentsToggle}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md-9">
                      <label className="form-label mb-3">Models or Groups</label>
                      <Select
                        isMulti
                        classNamePrefix="react-select"
                        value={selectedGroupOptions}
                        onChange={handleGroupSelectChange}
                        options={groupSelectOptions}
                        placeholder="Select asset groups"
                        isDisabled={emailGroupForm.allGroups}
                      />
                    </div>
                    <div className="col-12 col-md-3 d-flex align-items-center justify-content-between justify-content-md-end gap-2 mt-2 mb-8 mt-md-0">
                      <span className="text-sm">All Groups</span>
                      <div className="form-switch switch-primary d-flex align-items-center gap-2 mb-0 me-8">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={emailGroupForm.allGroups}
                          onChange={handleAllGroupsToggle}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md-9">
                      <label className="form-label mb-3">Classes</label>
                      <Select
                        isMulti
                        classNamePrefix="react-select"
                        value={selectedClassOptions}
                        onChange={handleClassSelectChange}
                        options={classSelectOptions}
                        placeholder="Select asset classes"
                        isDisabled={emailGroupForm.allClasses}
                      />
                    </div>
                    <div className="col-12 col-md-3 d-flex align-items-center justify-content-between justify-content-md-end gap-2 mt-2 mb-8 mt-md-0">
                      <span className="text-sm">All Classes</span>
                      <div className="form-switch switch-primary d-flex align-items-center gap-2 mb-0 me-8">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={emailGroupForm.allClasses}
                          onChange={handleAllClassesToggle}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <hr className="mt-36 mb-24"></hr>*/}
                <label className="form-label">Group Emails</label>
                {emailGroupForm.entries.map((entry, index) => {
                  const selectedOption = resolveSelectedEmailOption(entry);
                  const relatedUser =
                    entry.type === 'user' && entry.userId
                      ? users.find((u) => u.id === entry.userId)
                      : null;
                  return (
                    <div className="row g-2 align-items-center mb-3" key={`email-entry-${index}`}>
                      <div className="col-12 col-md-6">
                        <Select
                          classNamePrefix={`react-select ${emailGroupFormErrors.emails ? 'is-invalid' : ''}`}
                          value={selectedOption}
                          onChange={(option) => handleEmailSelectionChange(index, option)}
                          options={emailSelectOptions}
                          placeholder="Select or add an email"
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-4">
                        {entry.type === 'custom' && entry.name ? (
                          <>
                            <span className="me-8 badge bg-light text-dark text-sm">{entry.name}</span>
                            <span className="badge bg-alert-focus text-alert text-sm">External Email</span>
                          </>
                        ) : relatedUser?.name ? (
                          <>
                            <span className="badge bg-light text-dark text-sm">{relatedUser.name}</span>
                            <span className="badge bg-success-focus text-success text-sm">System User</span>
                          </>
                        ) : null}
                      </div>
                      <div className="col-12 col-md-2 d-flex justify-content-center">
                        {index > 0 && (
                          <button
                            type="button"
                            className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                            onClick={() => handleRemoveEmailRow(index)}
                          >
                            <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="btn btn-link px-0 text-decoration-none"
                  onClick={handleAddEmailRow}
                  disabled={emailLimitReached}
                >
                  + Add Another Email
                </button>
                {emailLimitReached && (
                  <div className="text-muted mt-1">
                    A maximum of 25 emails can be included in a list
                  </div>
                )}
                {emailGroupFormErrors.emails && (
                  <div className="text-danger mt-2">{emailGroupFormErrors.emails}</div>
                )}
                {emailGroupFormErrors.submit && (
                  <div className="text-danger mt-2">{emailGroupFormErrors.submit}</div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={handleCloseEmailGroupModal}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleEmailGroupSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCustomEmailModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content" style={{ marginTop: '10vh' }}>
              <div className="modal-header">
                <h5 className="modal-title">+ Add Outside Email Address</h5>
                <button type="button" className="btn-close" onClick={handleCustomEmailModalClose} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className={`form-control ${customEmailErrors.name ? 'is-invalid' : ''}`}
                    value={customEmailForm.name}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCustomEmailForm((prev) => ({ ...prev, name: value }));
                      setCustomEmailErrors((prev) => ({ ...prev, name: undefined }));
                    }}
                    placeholder="Contact name"
                  />
                  {customEmailErrors.name && (
                    <div className="invalid-feedback">{customEmailErrors.name}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className={`form-control ${customEmailErrors.email ? 'is-invalid' : ''}`}
                    value={customEmailForm.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCustomEmailForm((prev) => ({ ...prev, email: value }));
                      setCustomEmailErrors((prev) => ({ ...prev, email: undefined }));
                    }}
                    placeholder="user@example.com"
                  />
                  {customEmailErrors.email && (
                    <div className="invalid-feedback">{customEmailErrors.email}</div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={handleCustomEmailModalClose}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleCustomEmailSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserListLayer;
